import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Package, Loader2, ArrowRight, PackageOpen } from "lucide-react";
import { api, fmt, fmtDate, fmtDay } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { BuyAgainSection, ReorderButton, ReorderReminders } from "@/components/Reorder";

const STATUS_LABELS = {
  confirmed: "Order Confirmed",
  packed: "Packed",
  dispatched: "Dispatched",
  in_transit: "In Transit",
  out_for_delivery: "Out for Delivery",
  delivered: "Delivered",
  cancelled: "Cancelled",
};

export function StatusChip({ status }) {
  const tone = status === "delivered"
    ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-400"
    : status === "cancelled"
      ? "border-red-500/30 bg-red-500/10 text-red-400"
      : "border-amber-500/30 bg-amber-500/10 text-amber-400";
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[10px] font-bold uppercase tracking-wider ${tone}`} data-testid={`status-chip-${status}`}>
      <span className={`h-1 w-1 rounded-full bg-current ${status !== "delivered" && status !== "cancelled" ? "animate-blink" : ""}`} />
      {STATUS_LABELS[status] || status}
    </span>
  );
}

export default function OrdersPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [orders, setOrders] = useState(null);

  // "Buy Again" account-menu link lands here — scroll to the section once orders load.
  useEffect(() => {
    if (location.hash !== "#buy-again" || !orders?.length) return;
    const t = setTimeout(() => {
      const el = document.getElementById("buy-again");
      if (!el) return;
      if (window.__lenis) window.__lenis.scrollTo(el, { offset: -80, duration: 1 });
      else el.scrollIntoView({ behavior: "smooth" });
    }, 250);
    return () => clearTimeout(t);
  }, [location.hash, orders]);

  useEffect(() => {
    if (user === undefined) return;
    const load = async () => {
      if (user) {
        try {
          const { data } = await api.get("/orders");
          setOrders(data.map((o) => ({ ...o })));
          return;
        } catch {
          setOrders([]);
          return;
        }
      }
      const stored = JSON.parse(localStorage.getItem("packo_guest_orders") || "[]");
      const fetched = [];
      for (const ref of stored) {
        try {
          const { data } = await api.get(`/orders/${ref.id}`, { params: { access: ref.access } });
          fetched.push({ ...data, _access: ref.access });
        } catch { /* order may be gone */ }
      }
      setOrders(fetched);
    };
    load();
  }, [user]);

  return (
    <div className="min-h-screen pb-24 pt-28" data-testid="orders-page">
      <div className="mx-auto max-w-5xl px-6 lg:px-12">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <p className="mb-3 flex items-center gap-3 text-xs font-bold uppercase tracking-[0.25em] text-amber-500">
            <span className="h-px w-10 bg-amber-500" />
            Order History
          </p>
          <h1 className="font-display text-3xl font-semibold tracking-tight text-1 sm:text-4xl">Your Orders</h1>
        </motion.div>

        {user && orders !== null && orders.length > 0 && (
          <section className="mt-10" data-testid="reorder-hub">
            <div className="flex items-end justify-between gap-4">
              <div>
                <p className="flex items-center gap-3 text-xs font-bold uppercase tracking-[0.25em] text-amber-500">
                  <span className="h-px w-10 bg-amber-500" /> Reorder & Recent Purchases
                </p>
                <h2 className="mt-2 font-display text-2xl font-semibold tracking-tight text-1">Running low? Restock in one tap</h2>
              </div>
              <a href="#buy-again" data-testid="reorder-hub-buy-again-link"
                className="hidden shrink-0 text-xs font-bold text-amber-500 hover:text-amber-400 sm:block">
                Jump to Buy Again ↓
              </a>
            </div>
            <ReorderReminders />
            <div className="mt-4 grid gap-3 sm:grid-cols-3" data-testid="recent-purchases">
              {orders.slice(0, 3).map((o) => (
                <div key={`recent-${o.id}`} data-testid={`recent-purchase-${o.id}`}
                  className="flex items-center justify-between gap-3 rounded-2xl border border-line bg-surface p-4">
                  <div className="min-w-0">
                    <button onClick={() => navigate(`/orders/${o.id}`)} className="truncate font-mono text-xs font-bold text-amber-400 hover:text-amber-300">
                      {o.order_number}
                    </button>
                    <p className="mt-0.5 text-[11px] text-3">
                      {String(o.created_at).slice(0, 10)} · {(o.items || []).reduce((n, i) => n + (i.qty || 0), 0)} items · {fmt(o.total)}
                    </p>
                  </div>
                  <ReorderButton orderId={o.id} orderNumber={o.order_number} status={o.status} />
                </div>
              ))}
            </div>
          </section>
        )}

        {user && orders !== null && orders.length > 0 && <BuyAgainSection />}

        {orders === null ? (
          <div className="mt-12 space-y-4" data-testid="orders-loading">
            {[1, 2].map((i) => (
              <div key={i} className="animate-pulse rounded-2xl border border-line bg-surface p-6 shadow-card">
                <div className="flex items-start justify-between gap-4">
                  <div className="space-y-2">
                    <div className="h-6 w-36 rounded-lg bg-hover" />
                    <div className="h-3 w-24 rounded bg-hover" />
                  </div>
                  <div className="flex gap-2">
                    <div className="h-6 w-24 rounded-full bg-hover" />
                    <div className="h-6 w-20 rounded-full bg-hover" />
                  </div>
                </div>
                <div className="mt-5 flex items-center gap-4">
                  <div className="flex -space-x-3">
                    {[0, 1, 2].map((j) => <div key={j} className="h-12 w-12 rounded-lg border-2 border-[var(--pq-bg)] bg-hover" />)}
                  </div>
                  <div className="h-4 w-32 rounded bg-hover" />
                </div>
                <div className="mt-5 border-t border-line pt-4">
                  <div className="h-3 w-48 rounded bg-hover" />
                </div>
              </div>
            ))}
          </div>
        ) : orders.length === 0 ? (
          <div className="mt-12 flex flex-col items-center gap-4 rounded-2xl border border-line bg-surface py-24 text-center" data-testid="orders-empty">
            <span className="flex h-16 w-16 items-center justify-center rounded-2xl border border-line bg-hover text-amber-500">
              <PackageOpen className="h-7 w-7" strokeWidth={1.6} />
            </span>
            <h2 className="font-display text-2xl font-semibold tracking-tight">No orders yet</h2>
            <p className="max-w-sm text-sm text-3">
              {user ? "Your PAQVERO orders will appear here once you place your first order." : "Guest orders on this device appear here. Sign in to keep them everywhere."}
            </p>
            <button onClick={() => navigate("/shop")} className="mt-2 rounded-full bg-amber-500 px-7 py-3.5 text-sm font-bold text-black hover:bg-amber-400" data-testid="orders-empty-shop">
              Start Shopping
            </button>
          </div>
        ) : (
          <div className="mt-10 space-y-4" data-testid="orders-list">
            {orders.map((o, i) => (
              <motion.div
                key={o.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: Math.min(i * 0.06, 0.3), duration: 0.5 }}
                className="cursor-pointer rounded-2xl border border-line bg-surface p-6 shadow-card transition-all duration-300 hover:border-amber-500/25 hover:shadow-lift"
                onClick={() => navigate(`/orders/${o.id}${o._access ? `?access=${o._access}` : ""}`)}
                data-testid={`order-card-${o.order_number}`}
              >
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <p className="font-display text-xl font-semibold tracking-tight text-1">{o.order_number}</p>
                    <p className="mt-1 text-xs text-3">Placed {fmtDate(o.created_at)}</p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <StatusChip status={o.status} />
                    <span className={`rounded-full border px-3 py-1 text-[10px] font-bold uppercase tracking-wider ${
                      o.payment.status === "paid" ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-400" : "border-strong text-2"
                    }`}>
                      {o.payment.status === "paid" ? "Paid" : "COD — Pending"}
                    </span>
                  </div>
                </div>
                <div className="mt-4 flex flex-wrap items-center gap-4">
                  <div className="flex -space-x-3">
                    {o.items.slice(0, 4).map((item) => (
                      <img key={item.sku} src={item.image} alt="" className="h-12 w-12 rounded-lg border-2 border-[var(--pq-bg)] bg-white object-contain p-1" loading="lazy" />
                    ))}
                  </div>
                  <p className="text-sm text-2">
                    {o.items.reduce((s, i) => s + i.qty, 0)} items · <span className="font-bold text-1">{fmt(o.total)}</span>
                  </p>
                </div>
                <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-line pt-4">
                  <p className="text-xs text-3">
                    {o.estimated_delivery?.from && o.estimated_delivery?.to ? (
                      <>ETA <span className="font-semibold text-2">{fmtDay(o.estimated_delivery.from)} – {fmtDay(o.estimated_delivery.to)}</span></>
                    ) : (
                      <span className="font-semibold text-2">Delivered</span>
                    )}
                    {o.tracking_id && <> · <span className="font-mono text-2">{o.tracking_id}</span></>}
                  </p>
                  <div className="flex items-center gap-3" onClick={(e) => e.stopPropagation()}>
                    <ReorderButton orderId={o.id} orderNumber={o.order_number} status={o.status} />
                    <span className="flex items-center gap-1.5 text-xs font-bold text-amber-500">
                      <Package className="h-3.5 w-3.5" /> Track Order <ArrowRight className="h-3.5 w-3.5" />
                    </span>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
